import React, {
  Suspense,
  Fragment,
  lazy,
} from 'react';
import {
  Switch,
  Redirect,
  Route,
} from 'react-router-dom';
import MainLayout from 'src/layouts/MainLayout';
import DashboardLayout from 'src/layouts/DashboardLayout';
import LoadingScreen from 'src/components/LoadingScreen';
import AuthGuard from 'src/components/AuthGuard';
import GuestGuard from 'src/components/GuestGuard';

type Routes = {
  exact?: boolean;
  strict?: boolean;
  path?: string | string[];
  guard?: any;
  layout?: any;
  component?: any;
  routes?: Routes;
}[];

export const renderRoutes = (routes: Routes = []): JSX.Element => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={`${route.path}`}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <Guard>
                <Layout>
                  {route.routes
                    ? renderRoutes(route.routes)
                    : <Component {...props} />}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const routes: Routes = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    component: lazy(() => import('src/views/auth/LoginView')),
  },
  {
    path: '/app',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/app',
        component: () => <Redirect to="/app/dashboard" />,
      },
      {
        exact: true,
        path: '/app/dashboard',
        component: lazy(() => import('src/views/dashboard/DashboardView')),
      },
      {
        exact: true,
        path: '/app/passports',
        component: lazy(() => import('src/views/passports/PassportsListView')),
      },
      {
        exact: true,
        strict: true,
        path: '/app/passports/history/:mode',
        component: lazy(() => import('src/views/passports/PassportsHistory')),
      },
      {
        exact: true,
        strict: true,
        path: '/app/passports/preview',
        component: lazy(() => import('src/views/passports/PassportPreviewView')),
      },
      {
        exact: true,
        strict: true,
        path: '/app/passports/:passportId',
        component: lazy(() => import('src/views/passports/PassportView')),
      },
      {
        exact: true,
        path: '/app/package/:passportId',
        component: lazy(() => import('src/views/passports/PackagingView')),
      },
      {
        exact: true,
        path: '/app/repositories',
        component: lazy(() => import('src/views/placing/RepositoriesView')),
      },
      {
        exact: true,
        path: '/app/repositories/:repositoryId',
        component: lazy(() => import('src/views/placing/RepositoryView')),
      },
      {
        exact: true,
        path: '/app/repositories/:repositoryId/cells',
        component: lazy(() => import('src/views/placing/CellsView')),
      },
      {
        exact: true,
        path: '/app/repositories/:repositoryId/cells/:cellId',
        component: lazy(() => import('src/views/placing/RowsView')),
      },
      {
        exact: true,
        path: '/app/repositories/:repositoryId/cells/:cellId/rows/:rowId',
        component: lazy(() => import('src/views/placing/RowView')),
      },
      {
        exact: true,
        path: '/app/delivery-reports',
        component: lazy(() => import('src/views/deliveryReports/DeliveryReportsView')),
      },
      {
        exact: true,
        path: '/app/delivery-reports/:reportId',
        component: lazy(() => import('src/views/deliveryReports/DeliveryReportView')),
      },
      {
        exact: true,
        path: '/app/rao-log/',
        component: lazy(() => import('src/views/reports/RAOLogView')),
      },
      {
        exact: true,
        path: '/app/archive',
        component: lazy(() => import('src/views/archive/ArchiveView')),
      },
      {
        exact: true,
        path: '/app/archive/:id',
        component: lazy(() => import('src/views/archive/ArchiveView')),
      },
      {
        exact: true,
        path: '/app/archive/file/:id',
        component: lazy(() => import('src/views/archive/FileView')),
      },
      {
        exact: true,
        path: '/app/rao-density',
        component: lazy(() => import('src/views/reports/RAODensityView')),
      },
      {
        exact: true,
        path: '/app/oob',
        component: lazy(() => import('src/views/reports/OOBView')),
      },
      {
        exact: true,
        path: '/app/annual',
        component: lazy(() => import('src/views/reports/AnnualView')),
      },
      {
        exact: true,
        path: '/app/operative',
        component: lazy(() => import('src/views/reports/OperativeView')),
      },
      {
        exact: true,
        path: '/app/inventory',
        component: lazy(() => import('src/views/reports/InventoryView')),
      },
      {
        exact: true,
        path: '/app/transportation',
        component: lazy(() => import('src/views/reports/TransportationView')),
      },
      {
        exact: true,
        path: '/app/rao-density/:repositoryId',
        component: lazy(() => import('src/views/reports/RAODensityView/RAODensityView')),
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
  {
    path: '*',
    layout: MainLayout,
    routes: [
      {
        exact: true,
        path: '/',
        component: () => <Redirect to="/login" />,
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
];

export default routes;
